'use client'

import { useEffect } from 'react'
import { useAccessibility } from '@/lib/accessibility-context'
import { useLanguage } from '@/lib/language-context'
import {
  X, RotateCcw,
  ZoomIn, ZoomOut, AlignJustify,
  AlignLeft, Type, Waves,
  Palette, Moon, FlipHorizontal,
  Link, Volume2, Headphones,
  MousePointer, PauseCircle, ImageOff,
} from 'lucide-react'

interface PanelButtonProps {
  icon: React.ReactNode
  label: string
  active?: boolean
  onClick: () => void
}

function PanelButton({ icon, label, active, onClick }: PanelButtonProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`
        flex flex-col items-center justify-center gap-2 p-4 rounded-xl border transition-all duration-200 text-center
        ${active
          ? 'border-[#8B1E3F] bg-[#8B1E3F]/10 text-[#8B1E3F] shadow-sm'
          : 'border-gray-200 bg-white text-gray-600 hover:border-[#8B1E3F]/50 hover:bg-gray-50'
        }
      `}
    >
      <span className={`w-7 h-7 flex items-center justify-center ${active ? 'text-[#8B1E3F]' : 'text-gray-500'}`}>
        {icon}
      </span>
      <span className="text-xs font-medium leading-tight">{label}</span>
    </button>
  )
}

export function AccessibilityPanel() {
  const { state, toggle, increaseFontSize, decreaseFontSize, resetAll, isOpen, setIsOpen } = useAccessibility()
  const { t } = useLanguage()

  useEffect(() => {
    if (document.getElementById('acc-keyframes')) return
    const style = document.createElement('style')
    style.id = 'acc-keyframes'
    style.textContent = `@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`
    document.head.appendChild(style)
  }, [])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9998] bg-black/20 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-label={t('accessibilityOptions')}
        className="fixed top-0 right-0 bottom-0 z-[9999] w-80 bg-white shadow-2xl flex flex-col overflow-hidden"
        style={{ animation: 'slideInRight 0.25s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white sticky top-0">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-[#8B1E3F]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('accessibilityOptions')}
            </h2>
            <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">Ctrl+F2</span>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition-colors"
            aria-label="Close accessibility panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-1">
          <div className="grid grid-cols-3 gap-2">
            <PanelButton
              icon={<ZoomIn className="w-6 h-6" />}
              label={t('biggerText')}
              onClick={increaseFontSize}
            />
            <PanelButton
              icon={<ZoomOut className="w-6 h-6" />}
              label={t('smallerText')}
              onClick={decreaseFontSize}
            />
            <PanelButton
              icon={<AlignJustify className="w-6 h-6" />}
              label={t('textSpacing')}
              active={state.textSpacing}
              onClick={() => toggle('textSpacing')}
            />

            <PanelButton
              icon={<AlignLeft className="w-6 h-6" />}
              label={t('lineHeight')}
              active={state.lineHeight}
              onClick={() => toggle('lineHeight')}
            />
            <PanelButton
              icon={<Type className="w-6 h-6" />}
              label={t('dyslexiaFriendly')}
              active={state.dyslexia}
              onClick={() => toggle('dyslexia')}
            />
            <PanelButton
              icon={<Waves className="w-6 h-6" />}
              label={t('adhdMode')}
              active={state.adhd}
              onClick={() => toggle('adhd')}
            />

            <PanelButton
              icon={<Palette className="w-6 h-6" />}
              label={t('saturation')}
              active={state.saturation}
              onClick={() => toggle('saturation')}
            />
            <PanelButton
              icon={<Moon className="w-6 h-6" />}
              label={t('lightDark')}
              active={state.darkMode}
              onClick={() => toggle('darkMode')}
            />
            <PanelButton
              icon={<FlipHorizontal className="w-6 h-6" />}
              label={t('invertColors')}
              active={state.invertColors}
              onClick={() => toggle('invertColors')}
            />

            <PanelButton
              icon={<Link className="w-6 h-6" />}
              label={t('highlightLinks')}
              active={state.highlightLinks}
              onClick={() => toggle('highlightLinks')}
            />
            <PanelButton
              icon={<Volume2 className="w-6 h-6" />}
              label={t('textToSpeech')}
              active={state.textToSpeech}
              onClick={() => toggle('textToSpeech')}
            />
            <PanelButton
              icon={<Headphones className="w-6 h-6" />}
              label={t('screenReader')}
              active={state.screenReader}
              onClick={() => toggle('screenReader')}
            />

            <PanelButton
              icon={<MousePointer className="w-6 h-6" />}
              label={t('cursor')}
              active={state.bigCursor}
              onClick={() => toggle('bigCursor')}
            />
            <PanelButton
              icon={<PauseCircle className="w-6 h-6" />}
              label={t('pauseAnimation')}
              active={state.pauseAnimation}
              onClick={() => toggle('pauseAnimation')}
            />
            <PanelButton
              icon={<ImageOff className="w-6 h-6" />}
              label={t('hideImages')}
              active={state.hideImages}
              onClick={() => toggle('hideImages')}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-4 py-4 bg-white sticky bottom-0">
          <button
            onClick={resetAll}
            className="w-full flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            {t('resetAllSettings')}
          </button>
          <p className="text-center text-xs text-gray-400 mt-3">{t('createdBy')}</p>
        </div>
      </div>

    </>
  )
}
